// Relative path: src/utils/RecUtils.js

import { db } from "../firebase";
import { doc, getDoc, collection, query, where, getDocs, getCountFromServer, onSnapshot, limit } from "firebase/firestore";
import websiteUtils from "./WebsiteUtils";

/**
 * Recommendation state machine states
 */
const RecommendationState = {
  INITIAL: 'initial',
  WAITING_DOC: 'waiting_doc',
  WAITING_REC: 'waiting_rec',
  COMPLETED: 'completed'
};

/**
 * Recommendation algorithm status codes
 */
const AlgorithmStatus = {
  IN_PROGRESS: 0,
  COMPLETED: 1,
  FALLBACK: -1
};

/**
 * Time thresholds for state transitions (in milliseconds)
 */
const TimeThresholds = {
  WAITING_DOC: 30000,     // 30 seconds
  WAITING_REC: 300000     // 5 minutes
};

/**
 * Request Deduplicator - Prevents duplicate network requests
 */
class RequestDeduplicator {
  constructor() {
    this.inflightRequests = new Map();
  }

  async dedupe(key, requestFn) {
    if (this.inflightRequests.has(key)) {
      // Return existing promise
      return this.inflightRequests.get(key);
    }

    const promise = requestFn()
      .finally(() => {
        // Clean up after completion
        this.inflightRequests.delete(key);
      });

    this.inflightRequests.set(key, promise);
    return promise;
  }

  clear() {
    this.inflightRequests.clear();
  }
}

/**
 * Utility class for handling recommendation data fetched from Firestore
 * Enhanced with batching, deduplication, and progressive loading
 */
class RecommendationData {
  constructor() {
    this.recommendationDoc = null;
    this.recommendationId = null;
    this.clinicCache = {};
    this.doctorCache = {};
    this.reviewCache = {};
    this.error = null;
    this.totalReviewCount = null;
    
    // State machine properties
    this.currentState = RecommendationState.INITIAL;
    this.startTime = null;
    this.waitingRecStartTime = null;
    this.stateChangeCallback = null;
    this.docListener = null;
    this.isFallbackMode = false;
    
    // Timeout handlers
    this.waitingDocTimeoutId = null;
    this.waitingRecTimeoutId = null;
    
    // Optimized profile picture management
    this.usedProfilePictures = new Set();
    this.profilePictureAssignments = new Map();
    this.reviewerNameToPicture = new Map();
    
    // Performance optimization: Add reference counting for cleanup
    this.profilePictureRefCount = new Map();
    
    // Cache for frequently accessed data
    this.processedDataCache = new Map();

    // Network optimization components
    this.requestDeduplicator = new RequestDeduplicator();
    this.pendingReads = new Map();
    this.batchTimeout = null;
    this.hookTextCache = new Map();
    
    // Cache for clinic review counts
    this.clinicReviewCountCache = new Map();

    // Dynamic titles configuration cache
    this.dynamicTitlesConfig = null;
    this.formDataPreferencesConfig = null;
    
    // Used titles tracking per clinic option
    this.usedTitlesPerClinic = new Map();
  }

  /**
   * Initialize the recommendation state machine
   * @param {string} recommendationId - The recommendation ID to track
   * @param {function} stateChangeCallback - Callback function when state changes
   */
  initializeStateMachine(recommendationId, stateChangeCallback) {
    // Clear previous state
    this.cleanup();
    
    // Set up new state
    this.recommendationId = recommendationId;
    this.startTime = Date.now();
    this.currentState = RecommendationState.INITIAL;
    this.stateChangeCallback = stateChangeCallback;
    this.isFallbackMode = false;
    
    // Start the state machine
    this.executeCurrentState();
  }

  /**
   * Clean up listeners and reset state
   */
  cleanup() {
    // Clear any existing listeners
    if (this.docListener) {
      this.docListener();
      this.docListener = null;
    }
    
    // Clear any timeouts
    if (this.waitingDocTimeoutId) {
      clearTimeout(this.waitingDocTimeoutId);
      this.waitingDocTimeoutId = null;
    }
    
    if (this.waitingRecTimeoutId) {
      clearTimeout(this.waitingRecTimeoutId);
      this.waitingRecTimeoutId = null;
    }

    // Clear batch timeout
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    // Clear network optimization state
    this.requestDeduplicator.clear();
    this.pendingReads.clear();
    
    // Reset used titles tracking for new session
    this.usedTitlesPerClinic.clear();
  }

  /**
   * Execute the current state of the state machine
   */
  executeCurrentState() {
    switch (this.currentState) {
      case RecommendationState.INITIAL:
        this.executeInitialState();
        break;
      case RecommendationState.WAITING_DOC:
        this.executeWaitingDocState();
        break;
      case RecommendationState.WAITING_REC:
        this.executeWaitingRecState();
        break;
      case RecommendationState.COMPLETED:
        this.executeCompletedState();
        break;
    }
  }

  /**
   * Execute the Initial state logic
   * Just record start time and move to WaitingDoc
   */
  executeInitialState() {
    console.log('State: INITIAL');
    this.startTime = Date.now();
    this.transitionToState(RecommendationState.WAITING_DOC);
  }

  /**
   * Execute the WaitingDoc state logic
   * Check if document exists in recommendations collection
   */
  executeWaitingDocState() {
    console.log('State: WAITING_DOC');
    
    // Set up a hard timeout to ensure we don't get stuck
    this.waitingDocTimeoutId = setTimeout(() => {
      console.log("Document wait timeout exceeded (hard timeout), going to fallback mode");
      this.isFallbackMode = true;
      this.transitionToState(RecommendationState.COMPLETED);
    }, TimeThresholds.WAITING_DOC);
    
    // Set up a listener for the document
    const docRef = doc(db, "recommendations", this.recommendationId);
    
    this.docListener = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        // Document exists, clear timeout and move to WaitingRec
        if (this.waitingDocTimeoutId) {
          clearTimeout(this.waitingDocTimeoutId);
          this.waitingDocTimeoutId = null;
        }
        
        this.recommendationDoc = docSnap.data();
        this.transitionToState(RecommendationState.WAITING_REC);
      }
    }, (error) => {
      console.error("Error listening to document:", error);
      // On error, go to fallback mode
      this.isFallbackMode = true;
      this.transitionToState(RecommendationState.COMPLETED);
    });
  }

  /**
   * Execute the WaitingRec state logic
   * Check recommendation_algorithm_status
   */
  executeWaitingRecState() {
    console.log('State: WAITING_REC');
    
    // Record start time if first time entering this state
    if (!this.waitingRecStartTime) {
      this.waitingRecStartTime = Date.now();
    }
    
    // Set up a hard timeout for the recommendation wait
    this.waitingRecTimeoutId = setTimeout(() => {
      console.log("Recommendation wait timeout exceeded (hard timeout), going to fallback mode");
      this.isFallbackMode = true;
      this.transitionToState(RecommendationState.COMPLETED);
    }, TimeThresholds.WAITING_REC);

    // If we already have a listener from WaitingDoc, clear it
    if (this.docListener) {
      this.docListener();
      this.docListener = null;
    }

    // Set up a listener for status changes
    const docRef = doc(db, "recommendations", this.recommendationId);
    this.docListener = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        this.recommendationDoc = data;
        const status = data.recommendation_algorithm_status;

        // Check if status is completed or fallback
        if (status === AlgorithmStatus.COMPLETED || status === "completed OK" || status === AlgorithmStatus.FALLBACK) {
          // Status indicates completion, clear timeout and move to Completed state
          if (this.waitingRecTimeoutId) {
            clearTimeout(this.waitingRecTimeoutId);
            this.waitingRecTimeoutId = null;
          }
          
          this.transitionToState(RecommendationState.COMPLETED);
        }
      } else {
        // Document disappeared, strange case, go to fallback
        console.error("Document disappeared, going to fallback mode");
        this.isFallbackMode = true;
        this.transitionToState(RecommendationState.COMPLETED);
      }
    }, (error) => {
      console.error("Error listening to document:", error);
      this.isFallbackMode = true;
      this.transitionToState(RecommendationState.COMPLETED);
    });
  }

  /**
   * Execute the Completed state logic
   * Check final status and display data or fallback
   */
  executeCompletedState() {
    console.log('State: COMPLETED');
    // Clean up any listeners and timeouts
    this.cleanup();
    
    // If we're already in fallback mode, no need to check document
    if (this.isFallbackMode) {
      if (this.stateChangeCallback) {
        this.stateChangeCallback(this.currentState, true);
      }
      return;
    }

    // Otherwise, check the final status
    const status = this.getStatus();
    if (status === AlgorithmStatus.COMPLETED || status === "completed OK") {
      // Success case - recommendation is ready
      if (this.stateChangeCallback) {
        this.stateChangeCallback(this.currentState, false);
      }
    } else {
      // Fallback case - recommendation failed or timed out
      this.isFallbackMode = true;
      if (this.stateChangeCallback) {
        this.stateChangeCallback(this.currentState, true);
      }
    }
  }

  /**
   * Transition to a new state
   * @param {string} newState - The state to transition to
   */
  transitionToState(newState) {
    console.log(`Transitioning from ${this.currentState} to ${newState}`);
    this.currentState = newState;
    
    // Notify callback about state change
    if (this.stateChangeCallback) {
      this.stateChangeCallback(this.currentState, this.isFallbackMode);
    }
    
    // Execute the new state
    this.executeCurrentState();
  }

  /**
   * Get the current state
   * @returns {string} Current state
   */
  getCurrentState() {
    return this.currentState;
  }

  /**
   * Check if in fallback mode
   * @returns {boolean} True if in fallback mode
   */
  isInFallbackMode() {
    return this.isFallbackMode;
  }

  /**
   * Fetch dynamic titles configuration from parameters collection
   * @returns {Promise<Object>} Configuration object with dynamic_titles and form_data_preferences
   */
  async getDynamicTitlesConfig() {
    return this.requestDeduplicator.dedupe(
      'dynamic-titles-config',
      async () => {
        try {
          // Fetch both documents in parallel
          const [aiCommentaryDoc, formDataPreferencesDoc] = await Promise.all([
            getDoc(doc(db, "parameters", "ai_commentary")),
            getDoc(doc(db, "parameters", "form_data_preferences"))
          ]);

          let dynamicTitles = {};
          let formDataPreferences = {};

          if (aiCommentaryDoc.exists()) {
            const data = aiCommentaryDoc.data();
            dynamicTitles = data.dynamic_titles || {};
          }

          if (formDataPreferencesDoc.exists()) {
            formDataPreferences = formDataPreferencesDoc.data();
          }

          // Cache the configurations
          this.dynamicTitlesConfig = dynamicTitles;
          this.formDataPreferencesConfig = formDataPreferences;

          return {
            dynamicTitles,
            formDataPreferences
          };
        } catch (error) {
          console.error("Error fetching dynamic titles configuration:", error);
          return {
            dynamicTitles: {},
            formDataPreferences: {}
          };
        }
      }
    );
  }

  /**
   * Generate default titles in the specified order
   * @param {number} count - Number of titles needed
   * @returns {Array} Array of default titles
   */
  getDefaultTitles(count) {
    const defaultTitleOptions = [
      "Patient Notes",
      "User Experience", 
      "Treatment Insight",
      "Process Review"
    ];
    
    const titles = [];
    for (let i = 0; i < count; i++) {
      if (i < defaultTitleOptions.length) {
        titles.push(defaultTitleOptions[i]);
      } else {
        // If we need more than 4 titles, use "Patient Notes" for the rest
        titles.push("Patient Notes");
      }
    }
    
    // console.log(`DYNAMIC TITLES: Generated ${count} default titles:`, titles);
    return titles;
  }

  /**
   * Generate dynamic titles for reviewer references
   * @param {Object} aiCommentary - AI commentary data
   * @param {Object} reviewerMetadata - Reviewer metadata
   * @param {Object} formData - User form data with preferences
   * @param {string} clinicName - Clinic name for used titles tracking
   * @returns {Promise<Array>} Array of titles for each reviewer reference
   */
  async generateDynamicTitles(aiCommentary, reviewerMetadata, formData, clinicName) {
    try {
      // console.log('DYNAMIC TITLES: Starting generation for clinic:', clinicName);
      // console.log('DYNAMIC TITLES: FormData received:', formData);
      
      // Get configuration data
      const config = await this.getDynamicTitlesConfig();
      const { dynamicTitles, formDataPreferences } = config;
      
      // console.log('DYNAMIC TITLES: Configuration loaded - dynamic titles count:', Object.keys(dynamicTitles).length);

      // Create a unique key for this clinic's session to avoid conflicts
      const clinicSessionKey = `${clinicName}-${Date.now()}`;
      // console.log('DYNAMIC TITLES: Using clinic session key:', clinicSessionKey);

      // Initialize used titles tracking for this specific generation session
      const usedTitles = new Set();
      // console.log('DYNAMIC TITLES: Initialized fresh used titles set for this session');

      // Collect all ref indices
      const refIndices = new Set();
      Object.keys(aiCommentary).forEach(key => {
        if (key.startsWith('ref_') && key.includes('_')) {
          const parts = key.split('_');
          if (parts.length >= 2) refIndices.add(parts[1]);
        }
      });

      // console.log('DYNAMIC TITLES: Found ref indices:', [...refIndices].sort());
      
      // Log available AI commentary keys for debugging
      // console.log('DYNAMIC TITLES: Available AI commentary keys:', Object.keys(aiCommentary).filter(k => k.includes('quote_subjects')));

      // Generate titles for each reviewer reference
      const titles = [];
      const sortedRefIndices = [...refIndices].sort();

      for (let i = 0; i < sortedRefIndices.length; i++) {
        const idx = sortedRefIndices[i];
        // console.log(`DYNAMIC TITLES: Processing reviewer reference ${i + 1} (index: ${idx})`);
        // console.log(`DYNAMIC TITLES: Currently used titles:`, [...usedTitles]);
        
        const title = await this.generateTitleForReviewer(
          idx, 
          aiCommentary, 
          reviewerMetadata, 
          formData, 
          dynamicTitles, 
          formDataPreferences, 
          usedTitles
        );
        
        // console.log(`DYNAMIC TITLES: Generated title for reviewer ${i + 1}:`, title);
        titles.push(title);
        
        // Add title to used titles if it's not the default
        if (title && !title.includes('Reviewer Reference')) {
          usedTitles.add(title);
          // console.log('DYNAMIC TITLES: Added to used titles:', title);
        }
      }

      // console.log('DYNAMIC TITLES: Final titles array:', titles);
      // console.log('DYNAMIC TITLES: Final used titles:', [...usedTitles]);
      return titles;
    } catch (error) {
      console.error("DYNAMIC TITLES: Error generating dynamic titles:", error);
      // Return default titles as fallback
      const refIndices = new Set();
      Object.keys(aiCommentary).forEach(key => {
        if (key.startsWith('ref_') && key.includes('_')) {
          const parts = key.split('_');
          if (parts.length >= 2) refIndices.add(parts[1]);
        }
      });
      const fallbackTitles = this.getDefaultTitles(refIndices.size);
      // console.log('DYNAMIC TITLES: Using fallback titles:', fallbackTitles);
      return fallbackTitles;
    }
  }

  /**
   * Generate a title for a specific reviewer reference
   * @param {string} idx - Reviewer reference index
   * @param {Object} aiCommentary - AI commentary data
   * @param {Object} reviewerMetadata - Reviewer metadata
   * @param {Object} formData - User form data
   * @param {Object} dynamicTitles - Dynamic titles configuration
   * @param {Object} formDataPreferences - Form data preferences configuration
   * @param {Set} usedTitles - Set of already used titles
   * @returns {Promise<string>} Generated title
   */
  async generateTitleForReviewer(idx, aiCommentary, reviewerMetadata, formData, dynamicTitles, formDataPreferences, usedTitles) {
    try {
    // console.log(`DYNAMIC TITLES: Generating title for reviewer index ${idx}`);
      
      // Get reviewer data
      const reviewId = aiCommentary[`ref_${idx}_review_id`];
      const reviewData = reviewerMetadata[reviewId] || {};
      const satisfaction = reviewData.user_satisfaction;
      
    // console.log(`DYNAMIC TITLES: Review ID: ${reviewId}, Satisfaction: ${satisfaction}`);
      
      // Determine if we should look at positive or negative subject tag groups
      const satisfactionNum = typeof satisfaction === 'string' ? parseFloat(satisfaction) : satisfaction;
      const isPositive = satisfactionNum > 3;
      
    // console.log(`DYNAMIC TITLES: Satisfaction number: ${satisfactionNum}, Looking for positive subjects: ${isPositive}`);

      // Collect all subjects for this reviewer - FIXED KEY NAMES (singular "quote", not plural "quotes")
      const positiveSubjectsKey = `ref_${idx}_quote_subjects_positive`;
      const negativeSubjectsKey = `ref_${idx}_quote_subjects_negative`;
      
      const positiveSubjects = aiCommentary[positiveSubjectsKey] || [];
      const negativeSubjects = aiCommentary[negativeSubjectsKey] || [];
      const allSubjects = [...positiveSubjects, ...negativeSubjects];

    // console.log(`DYNAMIC TITLES: Positive subjects (${positiveSubjectsKey}):`, positiveSubjects);
    // console.log(`DYNAMIC TITLES: Negative subjects (${negativeSubjectsKey}):`, negativeSubjects);
    // console.log(`DYNAMIC TITLES: All subjects:`, allSubjects);

      if (allSubjects.length === 0) {
        // console.log('DYNAMIC TITLES: No subjects found, using default title');
        // Calculate which default title to use based on the index
        const indexNum = parseInt(idx);
        const defaultTitles = this.getDefaultTitles(indexNum + 1);
        return defaultTitles[indexNum - 1]; // Arrays are 0-indexed, but idx starts from 1
      }

      // Get enabled preferences sorted by priority
      const enabledPreferences = this.getEnabledPreferencesSortedByPriority(formData, formDataPreferences);
      // console.log('DYNAMIC TITLES: Enabled preferences:', enabledPreferences);

      // Phase 1: Try preference-based matching
      for (const preference of enabledPreferences) {
      // console.log(`DYNAMIC TITLES: Trying preference-based matching for: ${preference}`);
        const title = this.findTitleByPreference(preference, allSubjects, dynamicTitles, isPositive, usedTitles);
        if (title) {
        // console.log(`DYNAMIC TITLES: Found title via preference ${preference}:`, title);
          return title;
        }
      }

      // Phase 2: Try priority-based matching within positive/negative groups
      // console.log('DYNAMIC TITLES: Trying priority-based matching');
      const title = this.findTitleByPriority(allSubjects, dynamicTitles, isPositive, usedTitles);
      if (title) {
        // console.log('DYNAMIC TITLES: Found title via priority matching:', title);
        return title;
      }

      // Phase 3: Default fallback
      // console.log('DYNAMIC TITLES: Using default fallback title');
      const indexNum = parseInt(idx);
      const defaultTitles = this.getDefaultTitles(indexNum + 1);
      const defaultTitle = defaultTitles[indexNum - 1]; // Arrays are 0-indexed, but idx starts from 1
      // console.log('DYNAMIC TITLES: Selected default title:', defaultTitle);
      return defaultTitle;
    } catch (error) {
      console.error(`DYNAMIC TITLES: Error generating title for reviewer ${idx}:`, error);
      return `Reviewer Reference ${parseInt(idx) + 1}`;
    }
  }

  /**
   * Get enabled preferences sorted by priority
   * @param {Object} formData - User form data
   * @param {Object} formDataPreferences - Form data preferences configuration
   * @returns {Array} Array of enabled preference strings sorted by priority
   */
  getEnabledPreferencesSortedByPriority(formData, formDataPreferences) {
    // console.log('DYNAMIC TITLES: Getting enabled preferences from formData:', formData);
    // console.log('DYNAMIC TITLES: Form data preferences config:', formDataPreferences);
    
    const enabledPrefs = [];
    
    // Check each preference
    Object.keys(formDataPreferences).forEach(prefKey => {
      const isEnabled = formData[prefKey] === true;
    // console.log(`DYNAMIC TITLES: Preference ${prefKey}: enabled = ${isEnabled}`);
      
      if (isEnabled) {
        const priority = formDataPreferences[prefKey].priority || 999;
        enabledPrefs.push({ preference: prefKey, priority });
      // console.log(`DYNAMIC TITLES: Added enabled preference ${prefKey} with priority ${priority}`);
      }
    });

    // Sort by priority (lower number = higher priority)
    const sortedPrefs = enabledPrefs
      .sort((a, b) => a.priority - b.priority)
      .map(item => item.preference);
      
    // console.log('DYNAMIC TITLES: Enabled preferences sorted by priority:', sortedPrefs);
    return sortedPrefs;
  }

  /**
   * Find title by preference matching - Enhanced version
   */
  findTitleByPreference(preference, allSubjects, dynamicTitles, isPositive, usedTitles) {
  // console.log(`DYNAMIC TITLES: findTitleByPreference - preference: ${preference}, isPositive: ${isPositive}`);
    
    const matchingGroups = [];

    // Find all subject tag groups that match this preference and have matching subjects
    Object.keys(dynamicTitles).forEach(groupId => {
      const group = dynamicTitles[groupId];
      
      if (group.isPositive !== isPositive) return;
      
      const relatedPrefs = group.related_preferences || [];
      const isRelated = relatedPrefs.includes(preference) || relatedPrefs.includes("");
      
      if (!isRelated) return;
      
      const subjectTags = group.subject_tags || [];
      const hasMatchingSubject = allSubjects.some(subject => 
        subjectTags.some(tag => subject.toLowerCase().includes(tag.toLowerCase()))
      );
      
      if (hasMatchingSubject) {
        matchingGroups.push({
          groupId,
          group,
          priority: group.priority || 999
        });
      }
    });

  // console.log(`DYNAMIC TITLES: Found ${matchingGroups.length} matching groups for preference ${preference}`);

    // Sort by priority and try to find unused title
    matchingGroups.sort((a, b) => a.priority - b.priority);
    
    // Try each group until we find an unused title
    for (const { group, groupId } of matchingGroups) {
      const titles = group.titles || [];
    // console.log(`DYNAMIC TITLES: Checking titles in group ${groupId}:`, titles);
      
      for (const title of titles) {
      // console.log(`DYNAMIC TITLES: Checking title "${title}", is used:`, usedTitles.has(title));
        if (!usedTitles.has(title)) {
        // console.log(`DYNAMIC TITLES: Selected title "${title}" from group ${groupId}`);
          return title;
        }
      }
    }

  // console.log(`DYNAMIC TITLES: No unused titles found for preference ${preference} across ${matchingGroups.length} groups`);
    return null;
  }

  /**
   * Find title by priority-based matching
   * @param {Array} allSubjects - All subject strings for this reviewer
   * @param {Object} dynamicTitles - Dynamic titles configuration
   * @param {boolean} isPositive - Whether to look at positive subject tag groups
   * @param {Set} usedTitles - Set of used titles
   * @returns {string|null} Found title or null
   */
  findTitleByPriority(allSubjects, dynamicTitles, isPositive, usedTitles) {
  // console.log(`DYNAMIC TITLES: findTitleByPriority - isPositive: ${isPositive}`);
    
    const matchingGroups = [];

    // Find all subject tag groups that match our positive/negative requirement and have matching subjects
    Object.keys(dynamicTitles).forEach(groupId => {
      const group = dynamicTitles[groupId];
      
      // Check if this group matches our positive/negative requirement
      if (group.isPositive !== isPositive) {
        return;
      }
      
      // Check if any subjects match this group's subject tags
      const subjectTags = group.subject_tags || [];
      const hasMatchingSubject = allSubjects.some(subject => 
        subjectTags.some(tag => subject.toLowerCase().includes(tag.toLowerCase()))
      );
      
      if (hasMatchingSubject) {
        matchingGroups.push({
          groupId,
          group,
          priority: group.priority || 999
        });
      // console.log(`DYNAMIC TITLES: Priority matching - added group ${groupId} with priority ${group.priority}`);
      }
    });

  // console.log(`DYNAMIC TITLES: Found ${matchingGroups.length} matching groups for priority-based matching`);

    // Sort by priority and try to find unused title
    matchingGroups.sort((a, b) => a.priority - b.priority);
    
    for (const { group, groupId } of matchingGroups) {
      const titles = group.titles || [];
    // console.log(`DYNAMIC TITLES: Priority checking titles in group ${groupId}:`, titles);
      
      for (const title of titles) {
        if (!usedTitles.has(title)) {
        // console.log(`DYNAMIC TITLES: Priority selected title "${title}" from group ${groupId}`);
          return title;
        }
      }
    }

    // console.log('DYNAMIC TITLES: No unused titles found in priority-based matching');
    return null;
  }

  /**
   * Fetch recommendation document by ID with deduplication
   * @param {string} recommendation_id Recommendation ID
   * @returns {boolean} Success indicator
   */
  async fetchRecommendationFromId(recommendation_id) {
    return this.requestDeduplicator.dedupe(
      `recommendation-${recommendation_id}`,
      async () => {
        try {
          const docRef = doc(db, "recommendations", recommendation_id);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            this.recommendationDoc = docSnap.data();
            this.recommendationId = recommendation_id;
            this.error = null;
            return true;
          } else {
            this.error = "Recommendation not found";
            return false;
          }
        } catch (error) {
          this.error = `Error fetching recommendation: ${error.message}`;
          console.error("Error fetching recommendation:", error);
          return false;
        }
      }
    );
  }

  getRecommendationId() {
    return this.recommendationId;
  }

  getStatus() {
    if (!this.recommendationDoc) {
      this.error = "No recommendation data loaded";
      return null;
    }
    const status = this.recommendationDoc.recommendation_algorithm_status;
    if (status == null) {
      this.error = "Status field not found in recommendation data";
      return null;
    }
    return status;
  }

  getResultCount() {
    if (!this.recommendationDoc) {
      this.error = "No recommendation data loaded";
      return null;
    }
    const resultsTable = this.recommendationDoc.results_table;
    if (!resultsTable) {
      this.error = "Results table not found in recommendation data";
      return null;
    }
    const data = resultsTable.data;
    if (!Array.isArray(data)) {
      this.error = "Data array not found in results table";
      return null;
    }
    return data.length;
  }

  getResultClinicNames() {
    const resultCount = this.getResultCount();
    if (resultCount == null) return null;
    if (resultCount === 0) {
      this.error = "No results available";
      return null;
    }
    try {
      return this.recommendationDoc.results_table.data.map(item => {
        if (!item.clinic_only_name) throw new Error("Missing clinic_only_name");
        return item.clinic_only_name;
      });
    } catch (error) {
      this.error = `Error extracting clinic names: ${error.message}`;
      console.error(this.error);
      return null;
    }
  }

  getResultsOrderedByRank() {
    if (!this.recommendationDoc) {
      this.error = "No recommendation data loaded";
      return null;
    }
    try {
      const results = [...this.recommendationDoc.results_table.data];
      results.sort((a, b) => {
        const rankA = a.clinic_recommendation_rank ?? Number.MAX_SAFE_INTEGER;
        const rankB = b.clinic_recommendation_rank ?? Number.MAX_SAFE_INTEGER;
        return rankA - rankB;
      });
      return results;
    } catch (error) {
      this.error = `Error sorting results by rank: ${error.message}`;
      console.error(this.error);
      return null;
    }
  }

  getResultByRank(rank) {
    const results = this.getResultsOrderedByRank();
    if (!results) return null;
    return results.find(r => r.clinic_recommendation_rank === rank) || null;
  }

  /**
   * Get total review count for a clinic from the reviews collection
   * This counts ALL reviews for the clinic regardless of isVisible flag
   * @param {string} clinicName - Name of the clinic
   * @returns {Promise<number>} - Total number of reviews for the clinic
   */
  async getClinicTotalReviewCount(clinicName) {
    const cacheKey = `review-count-${clinicName}`;
    
    // Check cache first
    if (this.clinicReviewCountCache.has(cacheKey)) {
      return this.clinicReviewCountCache.get(cacheKey);
    }

    return this.requestDeduplicator.dedupe(
      cacheKey,
      async () => {
        try {
          const reviewsRef = collection(db, "reviews");
          const q = query(reviewsRef, where("clinic_only_name", "==", clinicName));
          const snapshot = await getCountFromServer(q);
          const count = snapshot.data().count;
          
          // Cache the result
          this.clinicReviewCountCache.set(cacheKey, count);
          return count;
        } catch (error) {
          console.error(`Error getting review count for clinic ${clinicName}:`, error);
          // Return 0 as fallback instead of throwing
          return 0;
        }
      }
    );
  }

  /**
   * Fetch clinic data with deduplication
   * @param {string} clinicName - Name of the clinic
   * @returns {Object|null} Clinic data
   */
  async fetchClinicFromName(clinicName) {
    if (this.clinicCache[clinicName]) {
      return this.clinicCache[clinicName];
    }

    return this.requestDeduplicator.dedupe(
      `clinic-${clinicName}`,
      async () => {
        try {
          const clinicsRef = collection(db, "clinics");
          const q = query(clinicsRef, where("clinic_only_name", "==", clinicName));
          const snapshot = await getDocs(q);
          if (snapshot.empty) {
            this.error = `Clinic not found: ${clinicName}`;
            return null;
          }
          const data = snapshot.docs[0].data();
          this.clinicCache[clinicName] = data;
          return data;
        } catch (error) {
          this.error = `Error fetching clinic data: ${error.message}`;
          console.error(this.error);
          return null;
        }
      }
    );
  }

  /**
   * Get fallback clinic data from parameters collection
   * @returns {Array} Array of fallback clinic data
   */
  async getFallbackClinicData() {
    return this.requestDeduplicator.dedupe(
      'fallback-clinic-data',
      async () => {
        try {
          const fallbackClinicsData = [];
          
          // Fetch fallback clinic IDs from parameters collection
          const parametersRef = doc(db, "parameters", "fallback");
          const parametersSnap = await getDoc(parametersRef);
          
          if (!parametersSnap.exists()) {
            this.error = "Fallback parameters not found";
            console.error("Fallback parameters document not found in parameters collection");
            // Fallback to default hardcoded clinics if parameters not found
            const defaultClinicNames = ['HLC', 'Resul Yaman', 'Heva Clinic'];
            
            for (let i = 0; i < defaultClinicNames.length; i++) {
              const clinicName = defaultClinicNames[i];
              const clinicData = await this.fetchClinicFromName(clinicName);
              
              if (clinicData) {
                const clinicId = clinicData.clinic_id;
                const doctors = clinicId != null
                  ? (await this.fetchDoctorsByClinicId(clinicId)) || []
                  : [];
                
                const mockRecommendationData = {
                  clinic_only_name: clinicName,
                  clinic_recommendation_rank: i + 1,
                  final_score: 'N/A'
                };
                
                fallbackClinicsData.push({
                  recommendationData: mockRecommendationData,
                  clinicData: clinicData || {},
                  doctors
                });
              }
            }
            
            return fallbackClinicsData;
          }
          
          // Get the fallback_clinic_ids array
          const fallbackClinicIds = parametersSnap.data().fallback_clinic_ids || [];
          
          // Use only the first 3 IDs
          const clinicIdsToUse = fallbackClinicIds.slice(0, 3);
          
          if (clinicIdsToUse.length === 0) {
            this.error = "No fallback clinic IDs configured";
            console.error("No fallback clinic IDs found in parameters document");
            return [];
          }
          
          // Fetch clinics by IDs in parallel
          const clinicPromises = clinicIdsToUse.map(async (clinicId, i) => {
            // Query the clinics collection by clinic_id
            const clinicsRef = collection(db, "clinics");
            const q = query(clinicsRef, where("clinic_id", "==", clinicId));
            const snapshot = await getDocs(q);
            
            if (!snapshot.empty) {
              const clinicData = snapshot.docs[0].data();
              
              // Fetch doctors for this clinic
              const doctors = (await this.fetchDoctorsByClinicId(clinicId)) || [];
              
              // Create a mock recommendation result
              const mockRecommendationData = {
                clinic_only_name: clinicData.clinic_only_name || "Unknown Clinic",
                clinic_recommendation_rank: i + 1,
                final_score: 'N/A'
              };
              
              return {
                recommendationData: mockRecommendationData,
                clinicData: clinicData || {},
                doctors
              };
            }
            return null;
          });

          const results = await Promise.all(clinicPromises);
          return results.filter(Boolean);
        } catch (error) {
          this.error = `Error fetching fallback clinic data: ${error.message}`;
          console.error(this.error);
          return [];
        }
      }
    );
  }

  /**
   * Fetch reviews for a clinic in fallback mode with batching optimization
   * @param {string} clinicName - Clinic name to fetch reviews for
   * @param {number} maxReviews - Maximum number of reviews to fetch
   * @returns {Array} - Array of reviews
   */
  async fetchFallbackReviews(clinicName, maxReviews = 20) {
    return this.requestDeduplicator.dedupe(
      `fallback-reviews-${clinicName}-${maxReviews}`,
      async () => {
        try {
          // First try with a direct query for reviews with satisfaction > 3 and isVisible = true
          const reviewsRef = collection(db, "reviews");
          let reviewsQuery;
          let snapshot;
          
          try {
            // Try with numeric comparison first and isVisible filter
            reviewsQuery = query(
              reviewsRef, 
              where("clinic_only_name", "==", clinicName),
              where("user_satisfaction", ">", 3),
              where("isVisible", "==", true),
              limit(maxReviews)
            );
            snapshot = await getDocs(reviewsQuery);
          } catch (error) {
            // If that fails, try without the user_satisfaction filter but keep isVisible
            console.error("Error querying with satisfaction filter:", error);
            try {
              reviewsQuery = query(
                reviewsRef,
                where("clinic_only_name", "==", clinicName),
                where("isVisible", "==", true),
                limit(maxReviews * 2) // Get more to filter client-side
              );
              snapshot = await getDocs(reviewsQuery);
            } catch (innerError) {
              // Last resort: try without isVisible filter but filter client-side
              console.error("Error querying with isVisible filter:", innerError);
              reviewsQuery = query(
                reviewsRef,
                where("clinic_only_name", "==", clinicName),
                limit(maxReviews * 2)
              );
              snapshot = await getDocs(reviewsQuery);
            }
          }
          
          if (snapshot.empty) {
            return [];
          }
          
          // Convert to array of review objects
          let reviews = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Filter for satisfaction > 3 and isVisible = true if we had to do fallback queries
          reviews = reviews.filter(review => {
            const satisfaction = typeof review.user_satisfaction === 'string'
              ? parseFloat(review.user_satisfaction)
              : review.user_satisfaction;
            
            // Check isVisible, default to true if the field doesn't exist
            const isVisible = review.isVisible !== false;
            
            return !isNaN(satisfaction) && satisfaction > 3 && isVisible;
          });
          
          // Sort by source priority (reddit first, then youtube/tiktok)
          reviews.sort((a, b) => {
            const siteA = (a.external_website || "").toLowerCase();
            const siteB = (b.external_website || "").toLowerCase();
            
            if (siteA.includes("reddit") && !siteB.includes("reddit")) return -1;
            if (!siteA.includes("reddit") && siteB.includes("reddit")) return 1;
            if ((siteA.includes("youtube") || siteA.includes("tiktok")) && 
                !(siteB.includes("youtube") || siteB.includes("tiktok"))) return -1;
            if (!(siteA.includes("youtube") || siteA.includes("tiktok")) && 
                (siteB.includes("youtube") || siteB.includes("tiktok"))) return 1;
            
            return 0;
          });
          
          // Limit to maxReviews
          return reviews.slice(0, maxReviews);
        } catch (error) {
          console.error(`Error fetching fallback reviews for ${clinicName}:`, error);
          return [];
        }
      }
    );
  }

  /**
   * Get all images from a clinic document, combining thumbnailImage and clinicImages arrays
   * @param {Object} clinicData - Clinic document data from Firestore
   * @returns {Array} - Array of image objects with downloadURL
   */
  getClinicGalleryImages(clinicData) {
    if (!clinicData) return [];
    
    const images = [];
    
    // Add thumbnailImage if available
    if (Array.isArray(clinicData.thumbnailImage)) {
      clinicData.thumbnailImage.forEach(img => {
        if (img && img.downloadURL) {
          images.push({
            downloadURL: img.downloadURL,
            isMainImage: true
          });
        }
      });
    }
    
    // Add clinicImages if available
    if (Array.isArray(clinicData.clinicImages)) {
      clinicData.clinicImages.forEach(img => {
        if (img && img.downloadURL) {
          images.push({
            downloadURL: img.downloadURL,
            isMainImage: false
          });
        }
      });
    }
    
    return images;
  }

  /**
   * Fetch doctors by clinic ID with deduplication
   * @param {string} clinicId - Clinic ID
   * @returns {Array|null} Array of doctor objects
   */
  async fetchDoctorsByClinicId(clinicId) {
    const key = `clinic_${clinicId}`;
    if (this.doctorCache[key]) return this.doctorCache[key];
    
    return this.requestDeduplicator.dedupe(
      `doctors-${clinicId}`,
      async () => {
        try {
          const doctorsRef = collection(db, "doctors");
          const q = query(doctorsRef, where("doctorClinic", "==", clinicId));
          const snapshot = await getDocs(q);
          const docs = snapshot.empty ? [] : snapshot.docs.map(d => d.data());
          this.doctorCache[key] = docs;
          return docs;
        } catch (error) {
          this.error = `Error fetching doctors data: ${error.message}`;
          console.error(this.error);
          return null;
        }
      }
    );
  }

  /**
   * Batch multiple review reads into single operation
   * @param {Array} reviewIds - Array of review IDs
   * @returns {Object} - Object mapping review IDs to review data
   */
  async batchedFetchReviews(reviewIds) {
    // Remove duplicates and already cached
    const uniqueIds = [...new Set(reviewIds)].filter(id => !this.reviewCache[id]);
    
    if (uniqueIds.length === 0) return {};

    // Use Firestore's batch read capabilities (limit of 10 per batch)
    const batch = uniqueIds.slice(0, 10);
    const promises = batch.map(id => this.fetchReviewFromId(id));
    
    // Execute all reads in parallel (Firebase best practice)
    const results = await Promise.allSettled(promises);
    
    const batchedResults = {};
    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        batchedResults[batch[index]] = result.value;
      }
    });
    
    return batchedResults;
  }

  /**
   * Debounced batch execution for review fetching
   * @param {string} reviewId - Review ID to fetch
   * @returns {Promise} - Promise that resolves with review data
   */
  scheduleReviewFetch(reviewId) {
    return new Promise((resolve, reject) => {
      if (!this.pendingReads.has(reviewId)) {
        this.pendingReads.set(reviewId, []);
      }
      
      this.pendingReads.get(reviewId).push({ resolve, reject });
      
      // Clear existing timeout
      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout);
      }
      
      // Batch requests with 50ms delay
      this.batchTimeout = setTimeout(() => {
        this.executeBatchedReads();
      }, 50);
    });
  }

  /**
   * Execute batched reads
   */
  async executeBatchedReads() {
    const reviewIds = Array.from(this.pendingReads.keys());
    const promises = this.pendingReads;
    this.pendingReads.clear();
    
    try {
      const results = await this.batchedFetchReviews(reviewIds);
      
      // Resolve all pending promises
      reviewIds.forEach(reviewId => {
        const promiseHandlers = promises.get(reviewId) || [];
        const result = results[reviewId];
        
        promiseHandlers.forEach(({ resolve, reject }) => {
          if (result) {
            resolve(result);
          } else {
            reject(new Error(`Review ${reviewId} not found`));
          }
        });
      });
    } catch (error) {
      // Reject all pending promises
      Array.from(promises.values()).flat().forEach(({ reject }) => {
        reject(error);
      });
    }
  }

  /**
   * Fetch review by ID with deduplication and batching
   * @param {string} review_id - Review ID
   * @returns {Object|null} Review data
   */
  async fetchReviewFromId(review_id) {
    if (this.reviewCache[review_id]) return this.reviewCache[review_id];
    
    return this.requestDeduplicator.dedupe(
      `review-${review_id}`,
      async () => {
        try {
          const docRef = doc(db, "reviews", review_id);
          const docSnap = await getDoc(docRef);
          if (!docSnap.exists()) {
            this.error = `Review not found: ${review_id}`;
            return null;
          }
          const data = docSnap.data();
          this.reviewCache[review_id] = data;
          return data;
        } catch (error) {
          this.error = `Error fetching review: ${error.message}`;
          console.error(this.error);
          return null;
        }
      }
    );
  }

  /**
   * Parse doctor review quotes which have format "[review_id] quote text"
   * @param {string} quoteString - Raw quote string 
   * @returns {Object|null} - Parsed object with reviewId and quoteText or null if invalid
   */
  parseDoctorReviewQuote(quoteString) {
    if (!quoteString || typeof quoteString !== 'string') return null;
    
    // Match pattern [reviewId] quoteText
    const match = quoteString.match(/^\[(.*?)\]\s+(.+)$/);
    if (!match) return null;
    
    return {
      reviewId: match[1],
      quoteText: match[2]
    };
  }

  /**
   * Process arrays of doctor review quotes (pros/cons)
   * @param {Array} quotesArray - Array of quote strings 
   * @returns {Array} - Array of parsed quote objects
   */
  processDoctorReviewQuotes(quotesArray) {
    if (!Array.isArray(quotesArray)) return [];
    
    return quotesArray
      .map(quote => this.parseDoctorReviewQuote(quote))
      .filter(Boolean); // Remove any null values
  }

  /**
   * Fetch all review data for an array of doctor quotes
   * @param {Array} parsedQuotes - Array of parsed quote objects with reviewId and quoteText
   * @returns {Object} - Object mapping reviewIds to groups of quotes with metadata
   */
  async fetchReviewsForDoctorQuotes(parsedQuotes) {
    if (!Array.isArray(parsedQuotes) || parsedQuotes.length === 0) return {};
    
    // Get unique review IDs
    const reviewIds = [...new Set(parsedQuotes.map(q => q.reviewId))];
    
    // Use batched fetching
    const reviewsData = await this.batchedFetchReviews(reviewIds);
    
    // Group quotes by review ID
    const quotesGrouped = {};
    
    reviewIds.forEach(reviewId => {
      if (!reviewsData[reviewId]) return;
      
      const reviewData = reviewsData[reviewId];
      const prosQuotes = parsedQuotes
        .filter(q => q.reviewId === reviewId && q.isPro)
        .map(q => q.quoteText);
      
      const consQuotes = parsedQuotes
        .filter(q => q.reviewId === reviewId && !q.isPro)
        .map(q => q.quoteText);
      
      quotesGrouped[reviewId] = {
        reviewData,
        prosQuotes,
        consQuotes
      };
    });
    
    return quotesGrouped;
  }

  /**
   * Get the count of unique reviews for a doctor
   * @param {Object} doctor - Doctor object with doctorReviewsPros and doctorReviewsCons arrays
   * @returns {number} - Count of unique reviews
   */
  getUniqueReviewCountOfDoctor(doctor) {
    if (!doctor) return 0;
    
    const doctorReviewsPros = Array.isArray(doctor.doctorReviewsPros) ? doctor.doctorReviewsPros : [];
    const doctorReviewsCons = Array.isArray(doctor.doctorReviewsCons) ? doctor.doctorReviewsCons : [];
    
    // Extract all review IDs from quotes
    const reviewIds = new Set();
    
    // Process pros quotes
    doctorReviewsPros.forEach(quoteStr => {
      const parsed = this.parseDoctorReviewQuote(quoteStr);
      if (parsed && parsed.reviewId) {
        reviewIds.add(parsed.reviewId);
      }
    });
    
    // Process cons quotes
    doctorReviewsCons.forEach(quoteStr => {
      const parsed = this.parseDoctorReviewQuote(quoteStr);
      if (parsed && parsed.reviewId) {
        reviewIds.add(parsed.reviewId);
      }
    });
    
    return reviewIds.size;
  }

  /**
   * Order doctors by review count (descending) and then alphabetically by name
   * @param {Array} doctors - Array of doctor objects
   * @returns {Array} - Sorted array of doctor objects
   */
  orderDoctors(doctors) {
    if (!Array.isArray(doctors) || doctors.length === 0) return [];
    
    // Create a copy of the doctors array to avoid modifying the original
    const sortedDoctors = [...doctors];
    
    // Sort by review count (descending) first, then by name alphabetically
    sortedDoctors.sort((a, b) => {
      const countA = this.getUniqueReviewCountOfDoctor(a);
      const countB = this.getUniqueReviewCountOfDoctor(b);
      
      // If review counts are different, sort by count (descending)
      if (countA !== countB) {
        return countB - countA;
      }
      
      // If review counts are the same, sort alphabetically by name
      const nameA = (a.doctorName || '').toLowerCase();
      const nameB = (b.doctorName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    return sortedDoctors;
  }

  /**
   * Fetch all clinic data with progressive loading
   * @returns {Array|null} Array of enriched clinic data
   */
  async fetchAllClinicData() {
    const results = this.getResultsOrderedByRank();
    if (!results) return null;
    
    try {
      // Progressive loading: fetch clinic data first, then doctors in parallel
      const clinicPromises = results.map(async result => {
        const name = result.clinic_only_name;
        if (!name) throw new Error("Missing clinic_only_name in result");
        
        // Stage 1: Get clinic data (critical)
        const clinicData = await this.fetchClinicFromName(name);
        return { result, clinicData, name };
      });

      const clinicsWithData = await Promise.all(clinicPromises);

      // Stage 2: Fetch doctors data in parallel (secondary)
      const enrichedPromises = clinicsWithData.map(async ({ result, clinicData, name }) => {
        const clinicId = clinicData?.clinic_id;
        const doctors = clinicId != null
          ? (await this.fetchDoctorsByClinicId(clinicId)) || []
          : [];
        
        return { 
          recommendationData: result, 
          clinicData: clinicData || {}, 
          doctors 
        };
      });

      return await Promise.all(enrichedPromises);
    } catch (error) {
      this.error = `Error fetching clinic data for all results: ${error.message}`;
      console.error(this.error);
      return null;
    }
  }

  /**
   * Find the justification reviews table for a specific clinic
   * @param {string} clinicName - The name of the clinic to find reviews for
   * @returns {Object|null} - The justification reviews table for the specified clinic or null if not found
   */
  findJustificationTableForClinic(clinicName) {
    if (!this.recommendationDoc || !this.recommendationDoc.clinic_justification_reviews_tables) {
      return null;
    }
    
    const tables = this.recommendationDoc.clinic_justification_reviews_tables;
    if (!Array.isArray(tables) || tables.length === 0) {
      return null;
    }
    
    // We need to search through each table (clinic) in the array
    for (const tableEntry of tables) {
      // Check if this table has a data field
      if (!tableEntry.data) continue;
      
      // Look through the reviews in the data field to find a matching clinic_only_name
      // We need to find at least one review that matches our target clinic
      let isClinicMatch = false;
      
      // Check if data is an array or an object with numeric keys
      const dataEntries = Array.isArray(tableEntry.data) 
        ? tableEntry.data 
        : Object.values(tableEntry.data);
      
      // Check if any review in this table belongs to our target clinic
      for (const review of dataEntries) {
        if (review.clinic_only_name === clinicName) {
          isClinicMatch = true;
          break;
        }
      }
      
      if (isClinicMatch) {
        return tableEntry;
      }
    }
    
    return null;
  }

  /**
   * Get justification reviews for a specific clinic from recommendation data
   * @param {string} clinicName - The name of the clinic
   * @returns {Array|null} - Array of justification review objects or null if not found
   */
  getJustificationReviewsForClinic(clinicName) {
    const clinicTable = this.findJustificationTableForClinic(clinicName);
    
    if (!clinicTable || !clinicTable.data) {
      return null;
    }
    
    // Convert table.data to array if it's an object with numeric keys
    const reviewsData = Array.isArray(clinicTable.data) 
      ? clinicTable.data 
      : Object.values(clinicTable.data);
    
    // Filter reviews to only include those that match our clinic
    // (just in case there are any mixed in from other clinics)
    return reviewsData.filter(review => review.clinic_only_name === clinicName);
  }

  /**
   * Get justification review stats for a clinic
   * @param {string} clinicName - The name of the clinic
   * @returns {Object} - Object with reviewCount, satisfiedCount, and satisfactionPercentage
   */
  getJustificationReviewStats(clinicName) {
    const justificationReviews = this.getJustificationReviewsForClinic(clinicName);
    
    if (!justificationReviews || justificationReviews.length === 0) {
      return {
        reviewCount: 0,
        satisfiedCount: 0,
        satisfactionPercentage: 0
      };
    }
    
    // Count total reviews and satisfied reviews (user_satisfaction > 3)
    const reviewCount = justificationReviews.length;
    let satisfiedCount = 0;
    
    justificationReviews.forEach(review => {
      // Check for user_satisfaction field, could be number or string
      const satisfaction = review.user_satisfaction;
      if (satisfaction !== undefined) {
        // Convert to number if it's a string
        const satisfactionValue = typeof satisfaction === 'string' 
          ? parseFloat(satisfaction) 
          : satisfaction;
        
        // Count as satisfied if greater than 3
        if (!isNaN(satisfactionValue) && satisfactionValue > 3) {
          satisfiedCount++;
        }
      }
    });
    
    // Calculate satisfaction percentage
    const satisfactionPercentage = reviewCount > 0 
      ? Math.round((satisfiedCount / reviewCount) * 100) 
      : 0;
    
    return {
      reviewCount,
      satisfiedCount,
      satisfactionPercentage
    };
  }

  /**
   * Get user input toggles from recommendation document with priority selection
   * @returns {Object} - Object with highest priority toggle from each group
   */
  getUserInputToggles() {
    if (!this.recommendationDoc || !this.recommendationDoc.user_input) {
      return { userToggle1: null, userToggle2: null };
    }
    
    const userInput = this.recommendationDoc.user_input;
    let userToggle1 = null;
    let userToggle2 = null;
    let priority1 = 0;
    let priority2 = 0;
    
    // Check toggles for group 1 with priorities
    if (userInput.surgeonsDoOperation === true && 2 > priority1) {
      userToggle1 = "High surgeon involvement";
      priority1 = 2;
    }
    
    if (userInput.singleOperationClinic === true && 1 > priority1) {
      userToggle1 = "Doctor does one operation per day";
      priority1 = 1;
    }
    
    // Check toggles for group 2 with priorities
    if (userInput.hadHairTransplant === true && 3 > priority2) {
      userToggle2 = "Many patients had prior transplants";
      priority2 = 3;
    }
    
    if (userInput.noMedicationAfter === true && 2 > priority2) {
      userToggle2 = "Many patients didn't use medication";
      priority2 = 2;
    }
    
    if (userInput.diffuseThinner === true && 1 > priority2) {
      userToggle2 = "Many patients are diffuse thinners";
      priority2 = 1;
    }
    
    return { userToggle1, userToggle2 };
  }

  /**
   * Format a list of strings according to grammar rules
   * @param {Array} items - Array of strings to format
   * @returns {string} - Formatted string with commas and 'and'
   */
  formatListWithGrammar(items) {
    if (!items || items.length === 0) {
      return "";
    }
    
    if (items.length === 1) {
      return items[0];
    }
    
    if (items.length === 2) {
      return `${items[0]} and ${items[1]}`;
    }
    
    // For 3 or more items, use commas and 'and'
    const lastItem = items[items.length - 1];
    const otherItems = items.slice(0, -1).join(", ");
    return `${otherItems}, and ${lastItem}`;
  }

  /**
   * Get total review count from Firestore with caching
   * @returns {Promise<number>} - Total number of reviews in the database
   */
  async getTotalReviewCount() {
    if (this.totalReviewCount !== null) {
      return this.totalReviewCount;
    }
    
    return this.requestDeduplicator.dedupe(
      'total-review-count',
      async () => {
        try {
          const reviewsCollection = collection(db, "reviews");
          const snapshot = await getCountFromServer(reviewsCollection);
          this.totalReviewCount = snapshot.data().count;
          return this.totalReviewCount;
        } catch (error) {
          console.error("Error getting total review count:", error);
          // Fallback to a reasonable default
          return 500;
        }
      }
    );
  }

  /**
   * Get dynamic hook texts for a clinic with caching
   * @param {string} clinicName - The name of the clinic
   * @returns {Promise<Object>} - Object with hookText and hookSubtext
   */
  async getHookTexts(clinicName) {
    // Check cache first
    if (this.hookTextCache.has(clinicName)) {
      return this.hookTextCache.get(clinicName);
    }

    return this.requestDeduplicator.dedupe(
      `hook-texts-${clinicName}`,
      async () => {
        // Get justification review stats
        const stats = this.getJustificationReviewStats(clinicName);
        
        // Get user toggles with priority selection
        const { userToggle1, userToggle2 } = this.getUserInputToggles();
        
        // Construct the hook text - just the percentage sentence
        const hookText = `${stats.satisfactionPercentage}% of patients similar to you loved this clinic`;
        
        // Get total review count
        const totalReviewCount = await this.getTotalReviewCount();
        
        // Construct hook subtext
        const hookSubtext = `found ${stats.reviewCount} patients from 1000+ reviews`;
        
        const result = {
          hookText,
          hookSubtext,
          toggles: {
            userToggle1,
            userToggle2
          }
        };

        // Cache the result
        this.hookTextCache.set(clinicName, result);
        return result;
      }
    );
  }

  /**
   * Calculate average satisfaction for a clinic based on justification reviews
   * @param {string} clinicName - The name of the clinic
   * @returns {number} - Average satisfaction rating (0-5)
   */
  getClinicAverageSatisfaction(clinicName) {
    const justificationReviews = this.getJustificationReviewsForClinic(clinicName);
    
    if (!justificationReviews || justificationReviews.length === 0) {
      return 4.5; // Default fallback value if no reviews
    }
    
    let totalSatisfaction = 0;
    let validReviewCount = 0;
    
    justificationReviews.forEach(review => {
      const satisfaction = review.user_satisfaction;
      if (satisfaction !== undefined) {
        // Convert to number if it's a string
        const satisfactionValue = typeof satisfaction === 'string' 
          ? parseFloat(satisfaction) 
          : satisfaction;
        
        // Only count valid satisfaction values
        if (!isNaN(satisfactionValue)) {
          totalSatisfaction += satisfactionValue;
          validReviewCount++;
        }
      }
    });
    
    // Calculate average satisfaction
    const averageSatisfaction = validReviewCount > 0 
      ? totalSatisfaction / validReviewCount 
      : 4.5; // Default fallback
    
    return averageSatisfaction;
  }

  /**
   * Get average satisfaction for all clinics from parameters with caching
   * @returns {Promise<number>} - Average satisfaction for all clinics
   */
  async getAllClinicsSatisfaction() {
    return this.requestDeduplicator.dedupe(
      'all-clinics-satisfaction',
      async () => {
        try {
          const paramsRef = doc(db, "parameters", "ai_commentary");
          const docSnap = await getDoc(paramsRef);
          
          if (docSnap.exists() && docSnap.data().all_clinics_satisfaction !== undefined) {
            const allClinicsSatisfaction = docSnap.data().all_clinics_satisfaction;
            return typeof allClinicsSatisfaction === 'string' 
              ? parseFloat(allClinicsSatisfaction) 
              : allClinicsSatisfaction;
          }
          
          // Default fallback value if not found
          return 3.2;
        } catch (error) {
          console.error("Error fetching all clinics satisfaction:", error);
          return 3.2; // Default fallback
        }
      }
    );
  }

  /**
   * Get satisfaction comparison data for a clinic with caching
   * @param {string} clinicName - The name of the clinic
   * @returns {Promise<Object>} - Object with clinicSatisfaction and avgSatisfaction
   */
  async getSatisfactionComparisonData(clinicName) {
    return this.requestDeduplicator.dedupe(
      `satisfaction-comparison-${clinicName}`,
      async () => {
        try {
          // Get clinic's average satisfaction
          const clinicSatisfaction = this.getClinicAverageSatisfaction(clinicName);
          
          // Get all clinics average satisfaction
          const allClinicsSatisfaction = await this.getAllClinicsSatisfaction();
          
          // Handle edge case: ensure clinic satisfaction is higher than all clinics
          let finalClinicSatisfaction = clinicSatisfaction;
          if (clinicSatisfaction <= allClinicsSatisfaction) {
            finalClinicSatisfaction = allClinicsSatisfaction + 0.2;
            // Cap at 5.0 if needed
            if (finalClinicSatisfaction > 5.0) {
              finalClinicSatisfaction = 5.0;
            }
          }
          
          return {
            clinicSatisfaction: parseFloat(finalClinicSatisfaction.toFixed(1)),
            avgSatisfaction: parseFloat(allClinicsSatisfaction.toFixed(1))
          };
        } catch (error) {
          console.error("Error getting satisfaction comparison data:", error);
          // Default fallback values
          return {
            clinicSatisfaction: 4.5,
            avgSatisfaction: 3.2
          };
        }
      }
    );
  }

  /**
   * Get website source information (name and logo) from review data
   * Common method used by AiCommentaryRenderer and FeaturedReviews
   * @param {Object} reviewData - Review document from Firestore
   * @param {Object} logoMap - Map of website keys to logo imports (optional - for backward compatibility)
   * @returns {Object} - Object with displayName, siteLogo, external_url, isExternal
   */
  getWebsiteSourceInfo(reviewData, logoMap = null) {
    // Use centralized websiteUtils instead of local mapping
    return websiteUtils.getWebsiteSourceInfo(reviewData);
  }

  /**
   * Generate dynamic labels based on form data and reviewer labels
   * Common method used by AiCommentaryRenderer and FeaturedReviews
   * @param {Object} formData - User input form data
   * @param {Array} reviewerLabels - Array of reviewer label strings (lowercase)
   * @returns {Array} - Array of label objects with text and positive properties
   */
  generateDynamicLabels(formData, reviewerLabels = []) {
    const dynamicLabels = [];
    
    if (formData) {
      if (formData.hadHairTransplant) {
        dynamicLabels.push({
          text: reviewerLabels.includes("previous_procedure") || reviewerLabels.includes("repair_patient")
            ? "had prior surgery"
            : "no prior surgery",
          positive: reviewerLabels.includes("previous_procedure") || reviewerLabels.includes("repair_patient")
        });
      }
      
      if (formData.diffuseThinner) {
        dynamicLabels.push({
          text: reviewerLabels.includes("diffuse_thinning")
            ? "diffuse thinner"
            : "not diffuse thinner",
          positive: reviewerLabels.includes("diffuse_thinning")
        });
      }
      
      if (formData.noMedicationAfter) {
        if (reviewerLabels.includes("medication_none")) {
          dynamicLabels.push({ text: "no medication", positive: true });
        } else {
          ["medication_finasteride","medication_minoxidil","medication_dutasteride"].forEach(med => {
            if (reviewerLabels.includes(med)) {
              dynamicLabels.push({ text: `used ${med.split('_')[1]}`, positive: false });
            }
          });
        }
      }
    }
    
    return dynamicLabels;
  }

  /**
   * Get highest similarity category for highlight text
   * @param {Object} similarityData - Similarity scores from cloud function
   * @returns {string} - Highlight text for bubble
   */
  getHighestSimilarityCategory(similarityData) {
    const categories = {
      hair_type_similarity: "similar hair type to you",
      hair_loss_level_similarity: "similar hair loss level to you", 
      age_similarity: "similar age to you",
      hair_color_similarity: "similar hair color to you"
    };

    let maxSimilarity = 0;
    let bestCategory = null;

    Object.keys(categories).forEach(key => {
      const similarity = parseFloat(similarityData[key] || 0);
      if (similarity > maxSimilarity && similarity > 0.5) {
        maxSimilarity = similarity;
        bestCategory = categories[key];
      }
    });

    return bestCategory || "Popular review worth checking";
  }

  /**
   * Extract before and after images from featuredReviewBeforeAfter array
   * @param {Array} beforeAfterArray - featuredReviewBeforeAfter array from review
   * @returns {Object} - Object with beforeImage and afterImage URLs
   */
  extractBeforeAfterImages(beforeAfterArray = []) {
    // console.log(`FEATURED REVIEWS: Extracting images from array with ${beforeAfterArray.length} entries`);
    
    let beforeImage = null;
    let afterImage = null;

    // First pass: Try to find images with -before/-after tags
    beforeAfterArray.forEach((entry, index) => {
      
      if (entry.name && entry.downloadURL) {
        // Check for before/after patterns in filename
        const nameLower = entry.name.toLowerCase();
        if (nameLower.includes('-before.') || nameLower.includes('_before.') || nameLower.includes('before.')) {
          beforeImage = entry.downloadURL;
          // console.log(`FEATURED REVIEWS: Found before image: ${entry.name}`);
        } else if (nameLower.includes('-after.') || nameLower.includes('_after.') || nameLower.includes('after.')) {
          afterImage = entry.downloadURL;
          // console.log(`FEATURED REVIEWS: Found after image: ${entry.name}`);
        }
      }
    });

    // If no before/after tags found, use array order (first = before, second = after)
    if ((!beforeImage || !afterImage) && beforeAfterArray.length >= 2) {
      // console.log(`FEATURED REVIEWS: No before/after tags found, using array order`);
      
      // Use first two images with valid downloadURL
      const validImages = beforeAfterArray.filter(entry => entry.downloadURL);
      
      if (validImages.length >= 2) {
        beforeImage = beforeImage || validImages[0].downloadURL;
        afterImage = afterImage || validImages[1].downloadURL;
        // console.log(`FEATURED REVIEWS: Using array order - before: ${validImages[0].name}, after: ${validImages[1].name}`);
      }
    }

    // console.log(`FEATURED REVIEWS: Image extraction result - before: ${!!beforeImage}, after: ${!!afterImage}`);
    return { beforeImage, afterImage };
  }

  /**
   * Extract featured review text from pros array (remove identifier)
   * @param {Array} prosArray - Pros array from review
   * @returns {string} - Clean review text without identifier
   */
  extractFeaturedReviewText(prosArray = []) {
    if (!prosArray || prosArray.length === 0) return '';
    
    const firstPros = prosArray[0];
    if (!firstPros || typeof firstPros !== 'string') return '';
    
    // Remove identifier in format [%...%] from beginning
    return firstPros.replace(/^\[%[^%]*%\]\s*/, '');
  }

/**
   * Fetch featured reviews for a specific clinic
   * @param {string} clinicName - Name of the clinic
   * @param {Object} formData - User input form data for label generation
   * @returns {Array} - Array of featured review objects ready for display
   */
  async fetchFeaturedReviewsForClinic(clinicName, formData = {}) {
    // console.log(`FEATURED REVIEWS: Starting fetch for clinic: ${clinicName}`);
    // console.log(`FEATURED REVIEWS: FormData:`, formData);
    
    try {
      // Get recommendation data to find similarity scores
      if (!this.recommendationDoc) {
        console.error("FEATURED REVIEWS: No recommendation data available");
        this.error = "No recommendation data available";
        return [];
      }

      // console.log(`FEATURED REVIEWS: Recommendation doc exists, looking for justification table`);

      // Find justification table for this clinic
      const justificationTable = this.findJustificationTableForClinic(clinicName);
      if (!justificationTable || !justificationTable.data) {
        // console.log(`FEATURED REVIEWS: No justification table found for clinic: ${clinicName}`);
        // console.log(`FEATURED REVIEWS: Available tables:`, this.recommendationDoc.clinic_justification_reviews_tables?.length || 0);
        return [];
      }

      // console.log(`FEATURED REVIEWS: Found justification table with ${justificationTable.data.length} entries`);

      // Get all review IDs for this clinic from justification table
      const reviewIds = justificationTable.data
        .filter(row => row.clinic_only_name === clinicName && row.review_id)
        .map(row => ({ 
          reviewId: row.review_id, 
          similarity: row.overall_similarity || 0,
          similarityData: row
        }));

      // console.log(`FEATURED REVIEWS: Found ${reviewIds.length} review IDs for clinic: ${clinicName}`);
      // console.log(`FEATURED REVIEWS: Review IDs:`, reviewIds.map(r => r.reviewId));

      if (reviewIds.length === 0) {
        // console.log(`FEATURED REVIEWS: No review IDs found for clinic: ${clinicName}`);
        return [];
      }

      // Fetch all reviews and filter for featured ones
      const featuredReviews = [];
      
      for (const { reviewId, similarity, similarityData } of reviewIds) {
        // console.log(`FEATURED REVIEWS: Processing review ${reviewId}`);
        
        try {
          const reviewData = await this.fetchReviewFromId(reviewId);
          
          if (!reviewData) {
            // console.log(`FEATURED REVIEWS: No data found for review ${reviewId}`);
            continue;
          }

          // console.log(`FEATURED REVIEWS: Review ${reviewId} - isFeaturedReview:`, reviewData.isFeaturedReview);
          // console.log(`FEATURED REVIEWS: Review ${reviewId} - isVisible:`, reviewData.isVisible);
          // console.log(`FEATURED REVIEWS: Review ${reviewId} - has featuredReviewBeforeAfter:`, !!reviewData.featuredReviewBeforeAfter);
          // console.log(`FEATURED REVIEWS: Review ${reviewId} - featuredReviewBeforeAfter length:`, reviewData.featuredReviewBeforeAfter?.length || 0);
          
          // Check if this is a featured review and is visible - more lenient boolean checking
          const isFeatured = reviewData.isFeaturedReview === true || reviewData.isFeaturedReview === 'true' || reviewData.isFeaturedReview === 1;
          const isVisible = reviewData.isVisible !== false && reviewData.isVisible !== 'false' && reviewData.isVisible !== 0;
          const hasBeforeAfter = reviewData.featuredReviewBeforeAfter && Array.isArray(reviewData.featuredReviewBeforeAfter) && reviewData.featuredReviewBeforeAfter.length > 0;

          // console.log(`FEATURED REVIEWS: Review ${reviewId} - processed flags: isFeatured=${isFeatured}, isVisible=${isVisible}, hasBeforeAfter=${hasBeforeAfter}`);
          
          if (isFeatured && isVisible && hasBeforeAfter) {
            // console.log(`FEATURED REVIEWS: Review ${reviewId} passed initial filters, extracting images`);
            
            // Extract before/after images
            const { beforeImage, afterImage } = this.extractBeforeAfterImages(reviewData.featuredReviewBeforeAfter);
            
            // console.log(`FEATURED REVIEWS: Review ${reviewId} - beforeImage:`, !!beforeImage);
            // console.log(`FEATURED REVIEWS: Review ${reviewId} - afterImage:`, !!afterImage);
            
            if (beforeImage && afterImage) {
              // console.log(`FEATURED REVIEWS: Review ${reviewId} has both before/after images, processing...`);
              
              // Get website source info using centralized method
              const websiteInfo = this.getWebsiteSourceInfo(reviewData);
              
              // Generate dynamic labels
              const reviewerLabels = (reviewData.labels || []).map(l => l.toLowerCase());
              const dynamicLabels = this.generateDynamicLabels(formData, reviewerLabels);
              
              // Get highlight text
              const highlightText = this.getHighestSimilarityCategory(similarityData);
              
              // Extract review text
              const reviewText = this.extractFeaturedReviewText(reviewData.pros);
              
              // Get author image
              const authorImage = reviewData.logoImage && reviewData.logoImage[0] 
                ? reviewData.logoImage[0].downloadURL 
                : null;
              
              // Process satisfaction rating
              const satisfaction = reviewData.user_satisfaction;
              const rating = typeof satisfaction === 'string' ? parseInt(satisfaction, 10) : (satisfaction || 4);
              
              // console.log(`FEATURED REVIEWS: Review ${reviewId} - processed data: rating=${rating}, highlightText="${highlightText}", reviewText length=${reviewText?.length || 0}`);
              
              featuredReviews.push({
                id: reviewId,
                reviewerName: reviewData.reviewer_name || "Unknown",
                reviewerAvatar: authorImage,
                sourceName: websiteInfo.displayName,
                sourceLogo: websiteInfo.siteLogo,
                beforeImage,
                afterImage,
                rating: isNaN(rating) ? 4 : Math.max(1, Math.min(5, rating)),
                maxRating: 5,
                tags: dynamicLabels, // Pass all labels with their positive/negative properties
                reviewText: reviewText || "Great experience overall.",
                highlightText,
                similarity,
                externalUrl: websiteInfo.external_url,
                originalData: reviewData,
                websiteInfo // Pass the website info for logo resolution in component
              });
              
              // console.log(`FEATURED REVIEWS: Successfully added review ${reviewId} to featured reviews`);
            } else {
              // console.log(`FEATURED REVIEWS: Review ${reviewId} missing before or after image`);
            }
          } else {
            // console.log(`FEATURED REVIEWS: Review ${reviewId} failed filters - isFeatured:${isFeatured}, isVisible:${isVisible}, hasBeforeAfter:${hasBeforeAfter}`);
          }
        } catch (error) {
          console.error(`FEATURED REVIEWS: Error processing review ${reviewId}:`, error);
        }
      }

      // console.log(`FEATURED REVIEWS: Found ${featuredReviews.length} featured reviews total`);

      // Sort by similarity and take top 2
      const sortedReviews = featuredReviews
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, 2);
        
      // console.log(`FEATURED REVIEWS: Returning ${sortedReviews.length} reviews after sorting and limiting`);
      return sortedReviews;
        
    } catch (error) {
      console.error(`FEATURED REVIEWS: Error fetching featured reviews for ${clinicName}:`, error);
      this.error = `Error fetching featured reviews: ${error.message}`;
      return [];
    }
  }
  
  /**
   * Format data for UI components, including doctor credentials and review quotes
   */
  getFormattedOptionData({ recommendationData, clinicData, doctors }) {
    // pick clinic thumbnail if available
    const thumbnailUrl =
      Array.isArray(clinicData.thumbnailImage) &&
      clinicData.thumbnailImage[0]?.downloadURL
        ? clinicData.thumbnailImage[0].downloadURL
        : null;
        
    // Get all gallery images for this clinic
    const galleryImages = this.getClinicGalleryImages(clinicData);

    // Format doctor objects with all required data
    const formattedDoctors = doctors && doctors.length > 0
      ? doctors.map(doc => ({
          doctorName: doc.doctorName || "Unknown Doctor",
          doctorTitle: doc.doctorTitle || "Cosmetic Surgeon, M.D.",
          doctorImageUrl:
            doc.doctorImage ||
            doc.doctorImageUrl ||
            "",
          doctorCredentials: Array.isArray(doc.doctorCredentials) ? doc.doctorCredentials : [],
          // Store the raw pros/cons review arrays
          doctorReviewsPros: Array.isArray(doc.doctorReviewsPros) ? doc.doctorReviewsPros : [],
          doctorReviewsCons: Array.isArray(doc.doctorReviewsCons) ? doc.doctorReviewsCons : []
        }))
      : null;

    // Order doctors by review count and name if doctors exist
    const orderedDoctors = formattedDoctors ? this.orderDoctors(formattedDoctors) : null;

    return {
      id: clinicData.clinic_id || Math.random().toString(36).substr(2, 9), // Fallback to random ID if clinic_id is missing
      title:
        clinicData.clinic_only_name ||
        recommendationData.clinic_only_name ||
        "Unknown Clinic",
      headerInfo: clinicData.clinicDescriptionShort || "No description available",
      headerInfoLong: clinicData.clinicDescriptionLong || "", // Add the long description
      estimatedCost: clinicData.estimatedCost || "Price unavailable",
      estimatedAvailability:
        clinicData.estimatedAvailability || "Availability unknown",
      rank: recommendationData.clinic_recommendation_rank || 0,
      ctaText: `Book ${
        clinicData.clinic_only_name ||
        recommendationData.clinic_only_name ||
        "This Clinic"
      }`,
      // Use the ordered doctors
      doctors: orderedDoctors,
      hasMoreDoctors: doctors && doctors.length > 4,
      imageUrl: thumbnailUrl || clinicData.clinicLogoUrl || "",
      galleryImages: galleryImages,
      hookText:
        "85% of all patients similar to you loved this clinic - mainly due to high surgeon involvement",
      hookSubtext: "300+ Verified Reviewers",
      rawRecommendationData: recommendationData,
      rawClinicData: clinicData
    };
  }

  /**
   * Optimized profile picture management with lazy loading support
   * @param {string} reviewerId - Unique ID for the reviewer
   * @returns {string} - Path to a profile picture
   */
  getProfilePictureForReviewer(reviewerId) {
    // If this reviewer already has an assignment, return it
    if (this.profilePictureAssignments.has(reviewerId)) {
      // Increment reference count
      const picturePath = this.profilePictureAssignments.get(reviewerId);
      const currentCount = this.profilePictureRefCount.get(picturePath) || 0;
      this.profilePictureRefCount.set(picturePath, currentCount + 1);
      return picturePath;
    }
    
    // First, check if we know the reviewer's name and already have a picture for that name
    const reviewData = this.reviewCache[reviewerId];
    if (reviewData && reviewData.reviewer_name) {
      const reviewerName = reviewData.reviewer_name;
      if (this.reviewerNameToPicture.has(reviewerName)) {
        // Use the same picture for this reviewer ID
        const picturePath = this.reviewerNameToPicture.get(reviewerName);
        this.profilePictureAssignments.set(reviewerId, picturePath);
        const currentCount = this.profilePictureRefCount.get(picturePath) || 0;
        this.profilePictureRefCount.set(picturePath, currentCount + 1);
        return picturePath;
      }
    }

    // Total number of available profile pictures
    const totalProfilePictures = 28;
    
    // Generate a list of available profile picture numbers
    const availableNumbers = [];
    for (let i = 1; i <= totalProfilePictures; i++) {
      if (!this.usedProfilePictures.has(i)) {
        availableNumbers.push(i);
      }
    }
    
    // If all pictures are used, reset and allow reuse
    if (availableNumbers.length === 0) {
      this.usedProfilePictures.clear();
      for (let i = 1; i <= totalProfilePictures; i++) {
        availableNumbers.push(i);
      }
    }
    
    // Select a random available profile picture
    const randomIndex = Math.floor(Math.random() * availableNumbers.length);
    const selectedNumber = availableNumbers[randomIndex];
    
    // Mark as used
    this.usedProfilePictures.add(selectedNumber);
    
    // Create the path to the profile picture
    const profilePicturePath = `pp${selectedNumber}.webp`;
    
    // Save the assignment for this reviewer
    this.profilePictureAssignments.set(reviewerId, profilePicturePath);
    
    // Initialize reference count
    this.profilePictureRefCount.set(profilePicturePath, 1);

    // If we have reviewer name info, also save by name for future consistency
    if (reviewData && reviewData.reviewer_name) {
      this.reviewerNameToPicture.set(reviewData.reviewer_name, profilePicturePath);
    }
    
    return profilePicturePath;
  }

  /**
   * Get a profile picture for a reviewer by name
   * @param {string} reviewerName - Name of the reviewer
   * @returns {string} - Path to a profile picture
   */
  getProfilePictureForReviewerByName(reviewerName) {
    // If this reviewer name already has an assignment, return it
    if (this.reviewerNameToPicture.has(reviewerName)) {
      const picturePath = this.reviewerNameToPicture.get(reviewerName);
      const currentCount = this.profilePictureRefCount.get(picturePath) || 0;
      this.profilePictureRefCount.set(picturePath, currentCount + 1);
      return picturePath;
    }
    
    // Total number of available profile pictures
    const totalProfilePictures = 28;
    
    // Generate a list of available profile picture numbers
    const availableNumbers = [];
    for (let i = 1; i <= totalProfilePictures; i++) {
      if (!this.usedProfilePictures.has(i)) {
        availableNumbers.push(i);
      }
    }
    
    // If all pictures are used, reset and allow reuse
    if (availableNumbers.length === 0) {
      this.usedProfilePictures.clear();
      for (let i = 1; i <= totalProfilePictures; i++) {
        availableNumbers.push(i);
      }
    }
    
    // Select a random available profile picture
    const randomIndex = Math.floor(Math.random() * availableNumbers.length);
    const selectedNumber = availableNumbers[randomIndex];
    
    // Mark as used
    this.usedProfilePictures.add(selectedNumber);
    
    // Create the path to the profile picture
    const profilePicturePath = `pp${selectedNumber}.webp`;
    
    // Save the assignment for this reviewer name
    this.reviewerNameToPicture.set(reviewerName, profilePicturePath);
    
    // Initialize reference count
    this.profilePictureRefCount.set(profilePicturePath, 1);
    
    return profilePicturePath;
  }

  /**
   * Optimized release function with reference counting
   * @param {string} reviewerId - The reviewer ID to release
   */
  releaseProfilePicture(reviewerId) {
    if (this.profilePictureAssignments.has(reviewerId)) {
      const picturePath = this.profilePictureAssignments.get(reviewerId);
      
      // Decrement reference count
      const currentCount = this.profilePictureRefCount.get(picturePath) || 1;
      const newCount = currentCount - 1;
      
      if (newCount <= 0) {
        // No more references, safe to release
        const pictureNumber = parseInt(picturePath.replace('pp', '').replace('.webp', ''));
        this.usedProfilePictures.delete(pictureNumber);
        this.profilePictureRefCount.delete(picturePath);
        
        // Remove from reviewer name mapping if this was the last reference
        const reviewData = this.reviewCache[reviewerId];
        const reviewerName = reviewData?.reviewer_name;
        if (reviewerName && this.reviewerNameToPicture.has(reviewerName)) {
          const nameProfilePath = this.reviewerNameToPicture.get(reviewerName);
          if (nameProfilePath === picturePath) {
            this.reviewerNameToPicture.delete(reviewerName);
          }
        }
      } else {
        // Still has references, just update count
        this.profilePictureRefCount.set(picturePath, newCount);
      }
      
      // Always remove the specific ID assignment
      this.profilePictureAssignments.delete(reviewerId);
    }
  }

  /**
   * Clear all profile picture assignments
   */
  clearProfilePictureAssignments() {
    this.usedProfilePictures.clear();
    this.profilePictureAssignments.clear();
    this.reviewerNameToPicture.clear();
    this.profilePictureRefCount.clear();
  }

  /**
   * Get website review breakdown for a clinic
   * @param {string} clinicName - The name of the clinic
   * @returns {Promise<Object>} - Object with website breakdown and total counts
   */
  async getClinicWebsiteReviewBreakdown(clinicName) {
    return this.requestDeduplicator.dedupe(
      `clinic-website-breakdown-${clinicName}`,
      async () => {
        try {
          // Get justification reviews for this clinic (used in normal mode)
          const justificationReviews = this.getJustificationReviewsForClinic(clinicName);
          let reviewsToAnalyze = [];

          if (justificationReviews && justificationReviews.length > 0) {
            // Normal mode - fetch the actual review documents
            const reviewIds = justificationReviews.map(r => r.review_id).filter(Boolean);
            const reviewPromises = reviewIds.map(id => this.fetchReviewFromId(id));
            const reviews = await Promise.all(reviewPromises);
            reviewsToAnalyze = reviews.filter(Boolean);
          } else {
            // Fallback mode - get reviews directly from reviews collection
            reviewsToAnalyze = await this.fetchFallbackReviews(clinicName, 50);
          }

          // Initialize website counts
          const websiteReviewCounts = {};
          const websiteKeys = websiteUtils.getWebsiteKeys();
          
          // Initialize all website keys to 0
          websiteKeys.forEach(key => {
            websiteReviewCounts[key] = 0;
          });
          websiteReviewCounts['match medic'] = 0;

          // Count reviews by website
          reviewsToAnalyze.forEach(review => {
            const websiteInfo = websiteUtils.getWebsiteSourceInfo(review);
            const websiteKey = websiteInfo.websiteKey;
            
            if (websiteReviewCounts.hasOwnProperty(websiteKey)) {
              websiteReviewCounts[websiteKey]++;
            }
          });

          // Remove websites with 0 count for cleaner data
          const filteredWebsiteReviewCounts = {};
          Object.keys(websiteReviewCounts).forEach(key => {
            if (websiteReviewCounts[key] > 0) {
              filteredWebsiteReviewCounts[key] = websiteReviewCounts[key];
            }
          });

          const totalReviewCount = reviewsToAnalyze.length;

          return {
            websiteReviewCounts: filteredWebsiteReviewCounts,
            totalReviewCount,
            reviewCount: totalReviewCount <= 10 ? "10+" : totalReviewCount.toString()
          };
        } catch (error) {
          console.error(`Error getting website breakdown for clinic ${clinicName}:`, error);
          return {
            websiteReviewCounts: {},
            totalReviewCount: 0,
            reviewCount: "0"
          };
        }
      }
    );
  }

  getError() {
    return this.error;
  }
}

const recUtils = new RecommendationData();
export default recUtils;
export { RecommendationData, RecommendationState, AlgorithmStatus };